import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Avatar, Box, Grid, Stack, Typography } from "@mui/material";
import { DashboardDraftJobsPaper, DashboardTitlePaper, boxStyles, jobBoxStyles } from "Components/Dashboard/Dashboard.styled";
import { Interviews } from "Components/Interviews/Interviews";
import { IJobsApiParams, JobsSortCols } from "Components/JobsTs/JobsModel";
import { ShCreateJobIcon, ShMyInterviewsIcon, ShMyJobsIcon, ShSettingsIcon } from 'assets/Icons';
import { useAppDispatch, useAppSelector } from "helpers/hooks";
import { useCallback, useEffect } from "react";
import { Link as RouterLink } from 'react-router-dom';
import { ISortParamsBase, RootState } from "shared/SharedModels";
import { ShButton, ShGreenBtn } from 'shared/SharedStyles/ShInputs';
import { ShIconPrimary, ShIconSecondary } from 'shared/SharedStyles/styleConstants';
import { DefaultPageSize } from "shared/constants";
import { resetJobDelete } from "store/slices/employer/jobs/jobs-list-actions-slice";
import { fetchJobsList } from "store/slices/employer/jobs/jobs-list-slice";

export const Dashboard = () => {

    const dispatch = useAppDispatch();
    const { firstName, middleName, lastName, accountAccess } = useAppSelector((state: RootState) => state.auth.auth);
    const { jobsList } = useAppSelector(state => state.employer.jobsList);

    // Get jobs to form payload and make the API call.
    const getJobs = useCallback((pageNo?: number, pageSize?: number,
        sortParams?: ISortParamsBase<JobsSortCols>, search?: string) => {
        // Reset delete state to avoid infinite rerenders if the function is toggled after deleting a Job.
        dispatch(resetJobDelete());
        const params: IJobsApiParams = {
            jobType: 'draft',
            pageNumber: pageNo ?? 1,
            pageSize: pageSize ?? DefaultPageSize,
            sortDirection: sortParams?.order ?? 'asc',
            sortColumn: sortParams?.orderBy ?? 'name',
            search: search ?? '',
        };
        dispatch(fetchJobsList(params));
    }, [dispatch]);

    // Get jobs list on page load.
    useEffect(() => {
        getJobs();
    }, [getJobs]);

    return (<>
        <Stack padding={{ xs: 1, sm: 1, md: 2, lg: 2 }}>
            <DashboardTitlePaper variant="outlined">
                <Stack flexDirection='row' alignItems='center' columnGap={2}>
                    <Avatar className="user-avatar">{(firstName && firstName[0]) + " " + (lastName && lastName[0])}</Avatar>
                    <Stack rowGap={2}>
                        <Typography variant="h4">Welcome {firstName + (middleName ? " " + middleName : "") + " " + lastName + " 🎉"}</Typography>
                        <Stack flexDirection='row' columnGap={2} flexWrap='wrap' rowGap={1}>
                            <ShButton variant="contained" size="small" disableElevation href="/employer/jobs">
                                <Typography whiteSpace='nowrap'>My Jobs</Typography>
                            </ShButton>
                            {!(accountAccess?.role === 'hiring_manager' || accountAccess?.role === 'observer') &&
                                <ShGreenBtn variant="contained" size="small" disableElevation color="success" href="/employer/job/new"
                                >
                                    <Typography whiteSpace='nowrap'>Create new Job</Typography>
                                </ShGreenBtn>
                            }
                        </Stack>
                    </Stack>
                </Stack>
            </DashboardTitlePaper>
        </Stack>

        <Stack padding={{ xs: 1, sm: 1, md: 2, lg: 2 }}>
            <DashboardTitlePaper>

                <Typography variant="h6" fontWeight="bold" >
                    Get Started! 💼
                </Typography>
                {/* Quick Start Bar */}
                <Typography variant="body2" color="primary" fontWeight="bold" paddingTop={1}>
                    You are minutes away from posting a job!
                </Typography>

                <Grid container spacing={1} marginTop={2}>
                    {!(accountAccess?.role === 'hiring_manager' || accountAccess?.role === 'observer') &&
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <Box component={RouterLink} to="/employer/job/new/title" display="flex" alignItems="center" sx={{ ...boxStyles }}>
                                <ShCreateJobIcon primaryColor={ShIconPrimary} secondaryColor={ShIconSecondary} />
                                <Typography variant="body2" color="textPrimary" fontWeight="bold" ml={2}>
                                    Create a Job
                                </Typography>
                            </Box>
                        </Grid>
                    }
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <Box component={RouterLink} to="/employer/jobs" display="flex" alignItems="center" sx={{ ...boxStyles }}>
                            <ShMyJobsIcon primaryColor={ShIconPrimary} secondaryColor={ShIconSecondary} />
                            <Typography variant="body2" color="textPrimary" fontWeight="bold" ml={4}>
                                See My Jobs
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <Box component={RouterLink} to="/employer/interviews" display="flex" alignItems="center" sx={{ ...boxStyles }}>
                            <ShMyInterviewsIcon primaryColor={ShIconPrimary} secondaryColor={ShIconSecondary} />
                            <Typography variant="body2" color="textPrimary" fontWeight="bold" ml={4}>
                                My Interviews
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <Box component={RouterLink} to="/employer/calendar" display="flex" alignItems="center" sx={{ ...boxStyles }}>
                            <CalendarMonthIcon style={{ color: ShIconPrimary, transform: 'scale(1.7)' }} />
                            <Typography variant="body2" color="textPrimary" fontWeight="bold" ml={4}>
                                My Calendar
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <Box component={RouterLink} to="/employer/settings" display="flex" alignItems="center" sx={{ ...boxStyles }}>
                            <ShSettingsIcon primaryColor={ShIconPrimary} secondaryColor={ShIconSecondary} />
                            <Typography variant="body2" color="textPrimary" fontWeight="bold" ml={4}>
                                Settings
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </DashboardTitlePaper>
        </Stack>

        <Grid container spacing={2} padding={{ xs: 1, sm: 1, md: 2, lg: 2 }}>
            <Grid item xs={12} sm={6}>
                <Box component={DashboardDraftJobsPaper}>
                    <Typography variant="h6" fontWeight="bold">Ready to Bring on Talent 🚀</Typography>
                    <Box display="flex" justifyContent="space-between" alignItems="center" marginTop={1}>
                        <Typography variant="body2" color="primary" fontWeight="bold">Finish Draft Jobs</Typography>
                        {jobsList && jobsList.length !== 0 && (
                            <ShButton variant="contained" color="primary" href="/employer/jobs">See all jobs</ShButton>
                        )}
                    </Box>
                     <Grid item container spacing={2} marginTop={4}>
                         <Stack spacing={2} sx={{ maxHeight: '300px', overflowY: 'auto', width: '100%' }}>
                            {jobsList && jobsList.length === 0 ? (
                                <Box display="flex" alignItems="center">
                                    <Typography variant="body1" color="textSecondary" fontWeight="bold" paddingLeft={2}> No Draft Jobs Yet. </Typography>
                                    <Box paddingLeft={2}>
                                        <ShGreenBtn variant="contained" size="small" disableElevation color="success" href="/employer/job/new">
                                            <Typography whiteSpace='nowrap'>Create new Job</Typography>
                                        </ShGreenBtn>
                                    </Box>
                                </Box>
                            ) : (
                                jobsList.map((job) => {
                                    // Parse hiring manager name to get initials
                                    const hiringManagerInitials = job.hiring_manager.split(" ").map((name) => name[0].toLocaleUpperCase()).join("");
                                    return (
                                        <Grid item xs={12} sm={12} key={job.id} component={RouterLink} to={`/employer/job/${job.id}/title`} display="flex"
                                        alignItems="center" sx={{ ...jobBoxStyles }}>
                                            {/* Avatar with hiring manager initials */}
                                            <Avatar>{hiringManagerInitials}</Avatar>
                                            <Typography variant="body2" color="textPrimary" fontWeight="bold" mr={16} ml={2}>
                                                {job.name}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary" fontWeight="bold" ml="auto">
                                                Hiring Manager: {job.hiring_manager}
                                            </Typography>
                                        </Grid>
                                    );
                                })
                            )}
                        </Stack>
                    </Grid>
                </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
                <DashboardDraftJobsPaper>
                    <Typography variant="h6" fontWeight="bold"> Upcoming Interview's 📌 </Typography>
                    <Box display="flex" justifyContent="space-between" alignItems="center" marginTop={1}>
                        <Typography variant="body2" color="primary" fontWeight="bold">Don't forget!</Typography>
                        <ShButton variant="contained" color="primary" href="/employer/interviews">See all Interviews</ShButton>
                    </Box>
                    <Grid container spacing={2} justifyContent="center">
                        <Grid item xs={12}>
                            <Stack marginTop={4} spacing={2} sx={{ maxHeight: '300px', overflowY: 'auto' }}>
                                <Interviews gridProps={{ xs: 12, sm: 6, md: 5, lg: 4, xl: 6 }} />
                            </Stack>
                        </Grid>
                    </Grid>
                </DashboardDraftJobsPaper>
            </Grid>
        </Grid>
    </>);
};
